import { LabeledField } from '@/components/form/labeled-field';
import { connectColors } from '@/theme/colors';
import { extractDropdownValue } from '@/utils/dropdown';
import { Dropdown, Icon } from '@lego/connect-components-react';
import { Toggle } from '@lego/connect-components-react/experimental';
import { Flex, Grid, Text } from '@lego/klik-ui';
import type React from 'react';
import { useState } from 'react';

type CalculationBasisOption = {
  id: string;
  label: string;
};

type AdditionalOptionsSectionProps = {
  calculationBasisValue?: string;
  autoCalcEnabled: boolean;
  calculationBasisOptions: CalculationBasisOption[];
  isSimulationSettingsInherited: boolean;
  onFieldChange: (field: string, value: string | boolean | undefined) => void;
};

export const AdditionalOptionsSection = ({
  calculationBasisValue,
  autoCalcEnabled,
  calculationBasisOptions,
  isSimulationSettingsInherited,
  onFieldChange,
}: AdditionalOptionsSectionProps) => {
  const [isExpanded, setIsExpanded] = useState(isSimulationSettingsInherited);
  return (
    <>
      <Flex
        alignItems="center"
        gap={2}
        cursor="pointer"
        onClick={() => setIsExpanded((prev) => !prev)}
        _hover={{ opacity: 0.7 }}
      >
        <Text className="label-lg-medium" style={{ cursor: 'pointer' }}>
          Calculation basis & Auto-calc (optional)
        </Text>
        <Icon
          aria-label="Arrow"
          name="chevron-down"
          size="small"
          style={{
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease-in-out',
          }}
        />
      </Flex>

      {isExpanded && (
        <Grid
          gridTemplateColumns="repeat(3, minmax(0, 1fr))"
          gap={4}
          width="100%"
          borderBottom={`1px solid ${connectColors.core.gray}`}
          paddingBottom={4}
        >
          <LabeledField label="Calculation basis">
            <Dropdown
              fullWidth
              placeholder="No Price List selected"
              value={calculationBasisValue ? [calculationBasisValue] : []}
              showClearButton
              onChange={(_event, value) =>
                onFieldChange(
                  'calculationBasisValue',
                  extractDropdownValue(value) || undefined,
                )
              }
            >
              {calculationBasisOptions.map((option) => (
                <Dropdown.Option key={option.id} value={option.id}>
                  {option.label}
                </Dropdown.Option>
              ))}
            </Dropdown>
          </LabeledField>

          <LabeledField label="Automatic calculation">
            <Flex alignItems="center" gap={1} minHeight="40px">
              <Toggle
                checked={autoCalcEnabled}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  onFieldChange('autoCalcEnabled', event.target.checked)
                }
                aria-label="Automatic calculation toggle"
                indicatorTextPosition="end"
                size="small"
                indicatorTextChecked="ON"
                indicatorTextUnchecked="OFF"
              />
            </Flex>
          </LabeledField>
        </Grid>
      )}
    </>
  );
};
